// Top-level app: auth gate, routing and the tenant shell.
import { useEffect, useState } from "react";
import type { TenantDetail } from "./api/types";
import type { AuthUser } from "./auth/auth";
import { useServices } from "./components/context";
import { ErrorBanner } from "./components/ui";
import { useLoad } from "./components/useLoad";
import { softCapNotice } from "./lib/usage";
import { parseRoute, routeHash, type Route, type RouteType, type TenantPage } from "./lib/router";
import { Audit } from "./pages/Audit";
import { Connect } from "./pages/Connect";
import { Grants } from "./pages/Grants";
import { Members } from "./pages/Members";
import { Namespaces } from "./pages/Namespaces";
import { Overview } from "./pages/Overview";
import { Packages } from "./pages/Packages";
import { Principals } from "./pages/Principals";
import { SignIn } from "./pages/SignIn";
import { Tenants } from "./pages/Tenants";
import { OpsDevice } from "./pages/OpsDevice";
import { Footer } from "./components/Footer";
import { ProfileMenu, type CurrentTenant } from "./components/ProfileMenu";
import { OPS_DEVICE_PATH, userCodeFromQuery } from "./api/ops";
import { Settings } from "./pages/Settings";
import { TrustConfigs } from "./pages/TrustConfigs";
import { VerifyEmail } from "./pages/VerifyEmail";

/** A sidebar link to a page, optionally with a package type, or an item that is not available yet. */
type NavItem = { label: string; page: TenantPage; type?: RouteType } | { label: string; later: string };

/**
 * The sidebar (ADR-0035 section 11). "Artifacts" is a heading that is always open. Crates and Containers are
 * filtered views of the one Packages page; npm shows "Coming later" and has no route. Release Variables gets its
 * own top-level item after Artifacts when it ships; it is not a package type.
 */
export const NAV: { heading?: string; items: NavItem[] }[] = [
  {
    items: [
      { label: "Overview", page: "overview" },
      { label: "Namespaces", page: "namespaces" },
    ],
  },
  {
    heading: "Artifacts",
    items: [
      { label: "Crates", page: "packages", type: "cargo" },
      { label: "Containers", page: "packages", type: "oci" },
      { label: "npm", later: "Coming later" },
    ],
  },
  {
    heading: "Access",
    items: [
      { label: "Trust configs", page: "trust-configs" },
      { label: "Principals", page: "principals" },
      { label: "Grants", page: "grants" },
      { label: "Members", page: "members" },
    ],
  },
  {
    items: [
      { label: "Connect", page: "connect" },
      { label: "Audit log", page: "audit" },
      { label: "Settings", page: "settings" },
    ],
  },
];

/** True if a sidebar link is the open page. Package links also match the type; Connect matches every tab. */
export function navCurrent(item: { page: TenantPage; type?: RouteType }, page: TenantPage, type?: RouteType) {
  return item.page === page && (page !== "packages" || item.type === type);
}

function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}

function TenantShell({
  slug,
  page,
  ns,
  type,
  repo,
  onTenant,
}: {
  slug: string;
  page: TenantPage;
  ns?: string;
  type?: RouteType;
  repo?: string;
  onTenant: (t: CurrentTenant | null) => void;
}) {
  const { api } = useServices();
  const tenant = useLoad(() => api.getTenant(slug), [slug]);
  const t = tenant.data;
  // Tell the profile menu which tenant is open and the caller's role in it.
  const current = t && t.slug === slug ? t : null;
  useEffect(() => {
    onTenant(
      current
        ? {
            slug: current.slug,
            name: current.name,
            role: current.role,
            plan: current.plan,
            trialEndsAt: current.trial_ends_at ?? null,
          }
        : null,
    );
    return () => onTenant(null);
  }, [current, onTenant]);
  return (
    <div className="tenant">
      <nav className="side" aria-label="Tenant pages">
        <p className="tenant-name">
          <strong>{t?.name ?? slug}</strong>
          <span className="mono small">{slug}</span>
        </p>
        {NAV.map((group, i) => (
          <div
            key={group.heading ?? i}
            className="nav-group"
            role={group.heading ? "group" : undefined}
            aria-labelledby={group.heading ? `nav-${i}` : undefined}
          >
            {group.heading && (
              <p className="nav-heading" id={`nav-${i}`}>
                {group.heading}
              </p>
            )}
            {group.items.map((item) =>
              "later" in item ? (
                <span key={item.label} className="nav-later" aria-disabled="true">
                  {item.label} <small>{item.later}</small>
                </span>
              ) : (
                <a
                  key={item.label}
                  href={routeHash({ name: "tenant", tenant: slug, page: item.page, type: item.type })}
                  aria-current={navCurrent(item, page, type) ? "page" : undefined}
                >
                  {item.label}
                </a>
              ),
            )}
          </div>
        ))}
      </nav>
      <div className="content">
        <ErrorBanner error={tenant.error} />
        {t && t.slug === slug && <SoftCapBanner tenant={t} />}
        {t && t.slug === slug && <TenantPageView page={page} tenant={t} ns={ns} type={type} repo={repo} />}
      </div>
    </div>
  );
}

function SoftCapBanner({ tenant }: { tenant: TenantDetail }) {
  const notice = softCapNotice(tenant);
  if (!notice) return null;
  return (
    <p
      className={`banner ${notice.level === 100 ? "error" : "warn"}`}
      role="status"
      data-level={notice.level}
    >
      {notice.text}
    </p>
  );
}

function TenantPageView({
  page,
  tenant,
  ns,
  type,
  repo,
}: {
  page: TenantPage;
  tenant: TenantDetail;
  ns?: string;
  type?: RouteType;
  repo?: string;
}) {
  switch (page) {
    case "overview":
      return <Overview tenant={tenant} />;
    case "namespaces":
      return <Namespaces tenant={tenant} />;
    case "trust-configs":
      return <TrustConfigs tenant={tenant} />;
    case "principals":
      return <Principals tenant={tenant} />;
    case "grants":
      // The key makes a new link with another namespace start the page again with that namespace.
      return <Grants key={ns ?? ""} tenant={tenant} ns={ns} />;
    case "members":
      return <Members tenant={tenant} />;
    case "packages":
      return <Packages tenant={tenant} type={type} repo={repo} />;
    case "connect":
      return <Connect key={ns ?? ""} tenant={tenant} ns={ns} type={type} />;
    case "audit":
      return <Audit tenant={tenant} />;
    case "settings":
      return <Settings tenant={tenant} />;
  }
}

/** The app with the footer on every page (sign-in and /ops/device too). */
export function App() {
  return (
    <div className="page">
      <AppBody />
      <Footer />
    </div>
  );
}

function AppBody() {
  const { auth } = useServices();
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);
  const route = useRoute();
  const [currentTenant, setCurrentTenant] = useState<CurrentTenant | null>(null);

  useEffect(() => auth.onChange(setUser), [auth]);

  if (user === undefined) return <p className="muted center">Loading...</p>;
  // The ops approval page (ADR-0029) has its own sign-in: operators use Google only.
  if (window.location.pathname === OPS_DEVICE_PATH) {
    return <OpsDevice user={user} initialCode={userCodeFromQuery(window.location.search)} />;
  }
  if (user === null) return <SignIn />;
  if (user.passwordAccount && !user.emailVerified) return <VerifyEmail user={user} onVerified={setUser} />;

  return (
    <div className="app">
      <header className="top">
        <a href="#/" className="brand">
          Prego
        </a>
        <span className="grow" />
        <ProfileMenu
          user={user}
          tenant={route.name === "tenant" && currentTenant?.slug === route.tenant ? currentTenant : null}
          onSignOut={() => void auth.signOut()}
        />
      </header>
      <main>
        {route.name === "tenant" ? (
          <TenantShell
            slug={route.tenant}
            page={route.page}
            ns={route.ns}
            type={route.type}
            repo={route.repo}
            onTenant={setCurrentTenant}
          />
        ) : (
          <Tenants />
        )}
      </main>
    </div>
  );
}
